import { COLORS } from './colors';

// Default styles that serve as the base configuration

//  NOTE : The design is the same for all dropdowns. If any changes are needed, they can be handled using variants.
const defaultStyles = {
  container: (provided, _state) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided, state) => ({
    ...provided,
    width: '100%',
    minHeight: '44px',
    borderRadius: '6px',
    fontSize: '14px !important',
    border: state.selectProps.hasError
      ? `1px solid #C03744`
      : '1px solid #E9EBF4',
    backgroundColor: state.isDisabled ? COLORS.secondary[25] : '#fff',
    boxShadow: 'none',
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : COLORS?.['secondary-700'],
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    '&:hover': state.isDisabled
      ? { cursor: 'not-allowed' }
      : state.selectProps?.hasError
        ? {
            cursor: 'pointer',
            borderColor: COLORS.error.default,
            backgroundColor: COLORS['pastel-1'].light,
          }
        : {
            cursor: 'pointer',
            borderColor: COLORS.secondary[300],
            backgroundColor: COLORS.secondary[25],
          },
    '&:focus-within': {
      backgroundColor: state.selectProps?.hasError
        ? '#FFEBE5'
        : state.isDisabled
          ? COLORS.secondary[25]
          : COLORS.secondary[25],
      borderColor: state.selectProps?.hasError
        ? ' #C03744'
        : state.isDisabled
          ? COLORS.secondary[100]
          : COLORS.secondary[300],
    },
    '&:has(input:focus:not([value=""]))': {
      backgroundColor: state.isDisabled
        ? COLORS.secondary[25]
        : '#fff !important',
    },
    '&:has-value': {
      backgroundColor: state.isDisabled
        ? COLORS.secondary[25]
        : '#fff !important',
    },
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    fontSize: '14px !important',
    padding: '6px !important',
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : state.isDisabled
        ? COLORS.secondary[500]
        : COLORS?.['secondary-700'],
    fontWeight: state.isDisabled ? 550 : 450,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? COLORS.secondary[25] : '',
    color: COLORS?.['secondary-700'],
    fontWeight: 450,
    fontSize: '14px',
    borderBottom: state.isLast ? 'none' : '1px solid #E9EBF4',
    padding: '12px !important',
    '&:hover': {
      backgroundColor: state.isDisabled ? '' : COLORS.secondary[50],
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    },
    ...(state.isDisabled && {
      opacity: 0.4,
      cursor: 'not-allowed',
    }),
  }),
  loadingMessage: (base) => ({
    ...base,
    padding: '5px',
    color: COLORS.secondary[700],
  }),
  multiValueRemove: (provided, state) => ({
    ...provided,
    color: state.selectProps?.hasError
      ? '#C03744 !important'
      : COLORS.secondary[700],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#ffffff',
      color: state.selectProps?.hasError
        ? '#C03744 !important'
        : COLORS.secondary[700],
    },
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : state.isDisabled
        ? COLORS.secondary[500]
        : COLORS.secondary[500],
    fontSize: '14px !important',
    fontWeight: state.isDisabled ? 550 : 'normal',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderWidth: '1px',
    borderRadius: '6px',
    borderColor: COLORS.border.color,
    marginTop: '3px',
    boxShadow:
      ' 0px 4px 10px 0px #D7DAEB1A, 0px 18px 18px 0px #D7DAEB17, 0px 40px 24px 0px #D7DAEB0D, 0px 71px 29px 0px #D7DAEB03, 0px 111px 31px 0px #D7DAEB00',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999999,
  }),
  menuList: (base) => ({ ...base, padding: '0px !important' }),

  singleValue: (provided, state) => ({
    ...provided,
    fontWeight: state.isDisabled ? 550 : 450,
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : state.isDisabled
        ? COLORS.secondary[500]
        : COLORS?.['secondary-700'],
    ...(state.selectProps?.hasIcon && { marginLeft: '8px' }),
    marginRight: '10px !important',
  }),
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    borderRadius: '6px',
    borderColor: state.selectProps?.hasError
      ? '#C03744 !important'
      : COLORS.border.color,
    padding: '0 8px',
    borderWidth: '1px',
    fontSize: '14px !important',
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : COLORS.secondary[700],
  }),
  multiValueLabel: (provided, state) => ({
    ...provided,
    color: state.selectProps?.hasError
      ? '#C03744 !important'
      : COLORS.secondary[700],
    fontSize: '14px !important',
  }),
  input: (provided, state) => ({
    ...provided,
    color: state.selectProps?.hasError
      ? ` #C03744 !important`
      : COLORS.secondary[700],
    backgroundColor: 'transparent',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.selectProps.hasError
      ? ` #C03744`
      : state.isDisabled
        ? COLORS.secondary[500]
        : COLORS.secondary[700],
    strokeWidth: 0,
    // Match the v1 / Address field chevron — react-select's default indicator
    // svg renders larger; cap it to 16px so every KSelect chevron is uniform.
    '& svg': {
      width: '16px',
      height: '16px',
    },
  }),
  indicatorSeparator: null,
  groupHeading: (provided) => ({
    ...provided,
    textTransform: 'none',
    padding: '8px 12px',
    marginBottom: '0px',
    backgroundColor: COLORS.secondary[25],
    borderBottom: `1px solid ${COLORS.border.color}`,
  }),
  group: (provided) => ({
    ...provided,
    paddingTop: '0px',
    paddingBottom: '0px',
  }),
};

// Available variants for different styling needs
const variants = {
  default: defaultStyles,

  form: {
    control: (provided) => ({
      ...provided,
      width: '100%',
      borderRadius: '6px',
      '&:hover': {
        borderColor: '#A0AEC0',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
  },
};

// Helper function to merge style objects
// Gives priority to override styles over base styles
const mergeStyles = (baseStyles, overrideStyles) => {
  const merged = { ...baseStyles };

  if (!overrideStyles) return merged;

  Object.keys(overrideStyles).forEach((key) => {
    if (typeof overrideStyles[key] === 'function') {
      const baseFn = merged[key] || ((provided) => provided);
      merged[key] = (provided, state) => ({
        ...baseFn(provided, state),
        ...overrideStyles[key](provided, state),
      });
    } else {
      merged[key] = overrideStyles[key];
    }
  });

  return merged;
};

export const KSelectStylesConfig = ({
  variant = 'default',
  customStyles = {},
}) => {
  // Get the variant styles or fall back to default if variant doesn't exist
  const variantStyles = variants[variant] || variants.default;

  // Merge variant styles with custom styles, giving priority to custom styles
  return mergeStyles(variantStyles, customStyles);
};

/*
Usage example:
1. Using default variant
const defaultSelect = KSelectStylesConfig({});

2. Using form variant
const formSelect = KSelectStylesConfig({
  variant: 'form'
});

3. Using custom styles with a variant
const customSelect = KSelectStylesConfig({
  variant: 'form',
  customStyles: {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'lightblue',
    })
  }
});
*/
